import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { AttendanceCalendar } from '@/components/AttendanceCalendar';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { SortableTh } from '@/components/ui/sortable-th';
import { useSortedRows, type SortAccessors } from '@/lib/sort';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import type { AttendanceRow } from '@bits/shared/zod';

export const peopleAttendanceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/people/attendance',
  component: PeopleAttendancePage,
});

type AttendanceSortKey =
  | 'person'
  | 'worked'
  | 'holidays'
  | 'weekends'
  | 'paidLeave'
  | 'lop'
  | 'paidDays';

const EMPTY_ROWS: AttendanceRow[] = [];

const ATTENDANCE_SORT_ACCESSORS: SortAccessors<AttendanceRow, AttendanceSortKey> = {
  person: (r) => r.personName ?? r.personId,
  worked: (r) => r.workingDaysX10,
  holidays: (r) => r.holidayDaysX10,
  weekends: (r) => r.weekendDaysX10,
  paidLeave: (r) => r.paidLeaveDaysX10,
  lop: (r) => r.lopDaysX10,
  paidDays: (r) => r.paidDaysX10,
};

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatDaysX10(x10: number): string {
  const whole = Math.trunc(x10 / 10);
  const frac = Math.abs(x10 % 10);
  return frac === 0 ? String(whole) : `${whole}.${frac}`;
}

function PeopleAttendancePage(): JSX.Element {
  const queryClient = useQueryClient();
  const [month, setMonth] = React.useState(currentMonth());
  const [error, setError] = React.useState<string | null>(null);
  const [unlockOpen, setUnlockOpen] = React.useState(false);
  const [unlockReason, setUnlockReason] = React.useState('');

  const monthQuery = useQuery({
    queryKey: ['attendance', month],
    queryFn: () => api.getAttendanceMonth(month),
    enabled: /^\d{4}-\d{2}$/.test(month),
  });

  const lockMutation = useMutation({
    mutationFn: () => api.lockAttendanceMonth(month),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['attendance', month] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const unlockMutation = useMutation({
    mutationFn: (reason: string) => api.unlockAttendanceMonth(month, { reason }),
    onSuccess: async () => {
      setUnlockOpen(false);
      setUnlockReason('');
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['attendance', month] });
    },
    onError: (err) => setError(toMessage(err)),
  });

  const data = monthQuery.data;
  const { rows, ...sorter } = useSortedRows(data?.rows ?? EMPTY_ROWS, ATTENDANCE_SORT_ACCESSORS);
  const totals = React.useMemo(() => {
    if (!data) return { paid: 0, lop: 0 };
    return data.rows.reduce(
      (acc, r) => ({
        paid: acc.paid + r.paidDaysX10,
        lop: acc.lop + r.lopDaysX10,
      }),
      { paid: 0, lop: 0 },
    );
  }, [data]);

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Attendance
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Per-person paid days for the month. Lock feeds payroll and satisfies the period-close gate.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="m">Month</Label>
            <Input
              id="m"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.currentTarget.value)}
              className="w-48"
            />
          </div>
          {data?.locked ? (
            <Dialog open={unlockOpen} onOpenChange={setUnlockOpen}>
              <DialogTrigger asChild>
                <Button variant="destructive">Unlock</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Unlock {month}</DialogTitle>
                  <DialogDescription>
                    Unlocking deletes the paid-days snapshot for this month. Not permitted once payroll has posted.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col gap-1.5">
                  <Label>Reason</Label>
                  <Input
                    value={unlockReason}
                    onChange={(e) => setUnlockReason(e.currentTarget.value)}
                    placeholder="e.g. missed a holiday declaration"
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant="destructive"
                    disabled={unlockReason.trim().length === 0 || unlockMutation.isPending}
                    onClick={() => unlockMutation.mutate(unlockReason.trim())}
                  >
                    {unlockMutation.isPending ? 'Unlocking…' : 'Unlock'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ) : (
            <Button
              disabled={
                lockMutation.isPending ||
                !data ||
                data.rows.length === 0 ||
                data.pendingApprovals > 0
              }
              onClick={() => lockMutation.mutate()}
            >
              {lockMutation.isPending ? 'Locking…' : 'Lock month'}
            </Button>
          )}
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Action failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <AttendanceCalendar month={month} />

      <Card>
        <CardHeader>
          <CardTitle>
            {month} · {data?.locked ? 'Locked' : 'Live'}
          </CardTitle>
          <CardDescription>
            {data ? (
              <>
                {data.rows.length} people · totals — paid {formatDaysX10(totals.paid)}d, LOP{' '}
                {formatDaysX10(totals.lop)}d
                {data.pendingApprovals > 0 ? (
                  <>
                    {' · '}
                    <span className="text-[hsl(var(--destructive))]">
                      {data.pendingApprovals} pending leave request(s) — cannot lock
                    </span>
                  </>
                ) : null}
                {data.lockedAt ? ` · locked ${new Date(data.lockedAt).toISOString().slice(0, 10)}` : ''}
              </>
            ) : (
              'Loading…'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!data ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Loading…</p>
          ) : data.rows.length === 0 ? (
            <p className="text-sm text-[hsl(var(--muted-foreground))]">
              No active people in {month}.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  <tr className="border-b border-[hsl(var(--border))]">
                    <SortableTh sortKey="person" controller={sorter} className="py-2 pr-4 font-medium">
                      Person
                    </SortableTh>
                    <SortableTh sortKey="worked" controller={sorter} className="py-2 pr-4 font-medium">
                      Worked
                    </SortableTh>
                    <SortableTh sortKey="holidays" controller={sorter} className="py-2 pr-4 font-medium">
                      Holidays
                    </SortableTh>
                    <SortableTh sortKey="weekends" controller={sorter} className="py-2 pr-4 font-medium">
                      Weekends
                    </SortableTh>
                    <SortableTh sortKey="paidLeave" controller={sorter} className="py-2 pr-4 font-medium">
                      Paid leave
                    </SortableTh>
                    <SortableTh sortKey="lop" controller={sorter} className="py-2 pr-4 font-medium">
                      LOP
                    </SortableTh>
                    <SortableTh sortKey="paidDays" controller={sorter} className="py-2 pr-4 font-medium">
                      Paid days
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.personId} className="border-b border-[hsl(var(--border))]/60">
                      <td className="py-2 pr-4">
                        <div className="font-medium">{r.personName ?? r.personId}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">
                          {r.joinDate}
                          {r.exitDate ? ` → ${r.exitDate}` : ''}
                        </div>
                      </td>
                      <td className="py-2 pr-4">{formatDaysX10(r.workingDaysX10)}</td>
                      <td className="py-2 pr-4">{formatDaysX10(r.holidayDaysX10)}</td>
                      <td className="py-2 pr-4">{formatDaysX10(r.weekendDaysX10)}</td>
                      <td className="py-2 pr-4">{formatDaysX10(r.paidLeaveDaysX10)}</td>
                      <td className="py-2 pr-4">
                        {r.lopDaysX10 > 0 ? (
                          <Badge variant="destructive">{formatDaysX10(r.lopDaysX10)}</Badge>
                        ) : (
                          <span>0</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 font-semibold">{formatDaysX10(r.paidDaysX10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AppShell>
  );
}
